/**
 * Route guard: renders its children only for a signed-in user whose
 * role matches `role`. Unauthenticated visitors go to `/login`;
 * signed-in users with the wrong role are bounced to `/`, which routes
 * them to their own role's home.
 */
import { Navigate, useLocation } from "react-router";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { AuthLoading } from "./AuthLoading";
import type { UserRole } from "../lib/types";

export function RequireRole({
  role,
  children,
}: {
  role: UserRole;
  children: ReactNode;
}) {
  const { status, currentUser } = useAuth();
  const location = useLocation();

  if (status === "loading") return <AuthLoading />;

  if (status !== "authed") {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (currentUser?.role !== role) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
